import { ChangeEvent, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Typography, useMediaQuery } from '@mui/material'
import axios from '../../../lib/axios'
import theme from 'theme'

/* Utils */
import {
  CategoryFramework,
  CPILibraryItem,
  FrameworkCategory,
  FrameworkCategoryOption,
  IFramework,
  IntegrationInstance,
  IntegrationStatusMessage
} from '../../../models'
import {
  defaultLibraryCategoriesNIST,
  defaultLibraryCategoriesOnyxia,
  defaultLibraryLabelDataSources,
  defaultLibraryStatusOptions
} from './library.constants'
import {
  hasActiveLibraryFilters,
  librarySortOptionLabels,
  LibraryStatus,
  resolveLibraryCategoryId,
  SORT_LABELS,
  sortFromLabel,
  toSearchFilters
} from './library.filters'
import { useLibraryFilterParams } from './useLibraryFilterParams'
import useDebounce from '../../../hooks/useDebounce'
import { useSearchCpis } from './utils'
import { breadcrumbs, isCustomFramework } from '../../../lib/utils'

/* Components */
import {
  ClearFilters,
  EmptyList,
  LibraryContainer,
  LibraryContentWrapper,
  ListWrapper,
  LoadingContainer,
  ResultsWrapper,
  SelectorsWrapper,
  ToolbarWrapper
} from './library.styles'
import LibrarySelectField from './components/LibrarySelectField'
import LibrarySelectLabelField, { LabelOption } from './components/LibrarySelectLabelField'
import LibraryCard from './components/LibraryCard'
import LibrarySearch from './components/LibrarySearch'
import LibraryCardLoading from './components/LibraryCardLoading'
import LibraryEmptyLogo from './components/LibraryEmptyLogo'
import { ThemeButton } from '../../components/buttons'
import HeaderComponent from '../../components/header/HeaderComponent'
import ToolbarComponent from '../../components/header/ToolbarComponent'
import ContentWrapper from '../../components/header/ContentWrapper'
import ClearIcon from '../../components/svg/ClearIcon'
import { useAppSelector } from '../../../store/hooks'
import { accountSettingsSelector } from '../../../store/user/userSlice'

export interface ErroredInstance {
  id: string
  status: IntegrationStatusMessage
  errorMessage?: string
}

const readSelectValues = (e: ChangeEvent<any>): string[] => {
  const value = e?.target?.value

  return Array.isArray(value) ? value : []
}

const collectSourceOptions = (cpis: CPILibraryItem[]): LabelOption[] => {
  const seen = new Set<string>()
  const options: LabelOption[] = []

  cpis.forEach((cpi) => {
    cpi.supported_integrations.forEach((integration) => {
      if (seen.has(integration.name)) return

      seen.add(integration.name)
      options.push({ label: integration.display_name, name: integration.name })
    })
  })

  return options
}

const Library: FC = () => {
  const account = useAppSelector(accountSettingsSelector)
  const framework = account?.cyFramework
  const resultsWrapper = useRef<HTMLDivElement | null>(null)

  const { filters, setFilter, resetFilter, clearFilters, write } = useLibraryFilterParams()

  const [categoryOptions, setCategoryOptions] = useState<FrameworkCategoryOption[]>(
    framework === CategoryFramework.NIST ? defaultLibraryCategoriesNIST : defaultLibraryCategoriesOnyxia
  )
  /* Two writers set the Data Source options: the catalog fetch on mount, and the pass that
   * narrows the options to the visible results. They keep separate state because the narrowed set
   * must take precedence. With one state, a late catalog response replaces the narrowed set and
   * shows every source again. */
  const [catalogSourceOptions, setCatalogSourceOptions] = useState<LabelOption[]>(defaultLibraryLabelDataSources)
  const [narrowedSourceOptions, setNarrowedSourceOptions] = useState<LabelOption[] | null>(null)
  const sourceOptions = narrowedSourceOptions ?? catalogSourceOptions
  const [customFrameworkCategories, setCustomFrameworkCategories] = useState<FrameworkCategory[]>([])
  const [configuredInstances, setConfiguredInstances] = useState<IntegrationInstance[]>([])
  const [erroredInstances, setErroredInstances] = useState<ErroredInstance[]>([])
  const [frameworkLoaded, setFrameworkLoaded] = useState(false)
  /* True after the API returns the categories. The canonicalisation effect below must not use the
   * default options above. */
  const [categoriesLoaded, setCategoriesLoaded] = useState(false)

  /* The term is the one filter with local state, because the box must show each keystroke. The
   * URL gets the debounced value. */
  const [searchInput, setSearchInput] = useState(filters.term)
  const debouncedSearch = useDebounce(searchInput, 350)
  const lastWrittenTerm = useRef(filters.term)

  const isSmallHeight = useMediaQuery('(max-height:870px)')
  const isMediumHeight = useMediaQuery('(max-height:1070px)')

  const { isFetching, isLoading, refetch, data: response } = useSearchCpis({
    value: filters.term,
    filters: toSearchFilters(filters),
    categoryIds: filters.categoryIds,
    categoryOptions,
    reset: false,
    staleTime: 1000 * 60,
    keepPreviousData: true,
    enabled: true
  })

  /* Memoised because a new fallback array on each render makes the narrowing effect below run
   * again and set state in a loop. */
  const data: CPILibraryItem[] = useMemo(() => response?.data.data.cpis || [], [response])
  const showSkeletons = isLoading || isFetching

  /* `setFilter` gets a new identity after each URL write. As a dependency of the effect below,
   * it makes that effect run again and write a term that the user cleared. */
  const setFilterRef = useRef(setFilter)
  setFilterRef.current = setFilter

  useEffect(() => {
    /* Write the trimmed term, because the URL parses back to that form. With an untrimmed term,
     * the resync effect below deletes the space that the user typed. */
    const term = debouncedSearch.trim()
    if (term === lastWrittenTerm.current) return

    lastWrittenTerm.current = term
    /* Replace the history entry. A push adds one entry for each keystroke. */
    setFilterRef.current('term', term, { replace: true })
  }, [debouncedSearch])

  useEffect(() => {
    if (filters.term === lastWrittenTerm.current) return

    /* The URL changed from outside this page: Back, Forward, or a new link. Copy the term into
     * the box. */
    lastWrittenTerm.current = filters.term
    setSearchInput(filters.term)
  }, [filters.term])

  /* The URL holds category ids. The control needs display titles. */
  const selectedCategoryTitles = useMemo(
    () => categoryOptions.filter((option) => filters.categoryIds.includes(option.id)).map((option) => option.title),
    [categoryOptions, filters.categoryIds]
  )

  /* Links from the dashboard and the category pages carry a slug or a display title. Rewrite
   * them to category ids, so that every copied URL is canonical. Wait for the fetched options.
   * The default options resolve a custom framework category to the wrong Onyxia id, and the
   * rewrite replaces the history entry. The first search request can still carry the unresolved
   * value, and the rewrite then causes one more fetch. */
  useEffect(() => {
    if (!categoriesLoaded || filters.categoryIds.length === 0) return

    const resolved = filters.categoryIds.map((id) => resolveLibraryCategoryId(id, categoryOptions))
    if (resolved.every((id, index) => id === filters.categoryIds[index])) return

    write({ ...filters, categoryIds: resolved }, { replace: true })
  }, [categoriesLoaded, categoryOptions, filters, write])

  const handleCategoryChange = (e: ChangeEvent<any>) => {
    const titles = readSelectValues(e)
    const ids = categoryOptions.filter((option) => titles.includes(option.title)).map((option) => option.id)
    /* With no options, no title maps back to an id. Keep the value from the link. */
    const unresolved = categoryOptions.length === 0 ? filters.categoryIds : []

    setFilter('categoryIds', Array.from(new Set([...ids, ...unresolved])))
  }

  const handleSourceChange = (e: ChangeEvent<any>) => setFilter('sources', readSelectValues(e))

  const handleStatusChange = (e: ChangeEvent<any>) => setFilter('statuses', readSelectValues(e) as LibraryStatus[])

  const handleSortChange = (e: ChangeEvent<any>) => setFilter('sort', sortFromLabel(e?.target?.value ?? ''))

  const handleClearAll = () => {
    setSearchInput('')
    lastWrittenTerm.current = ''
    clearFilters()
  }

  const uniqueConfiguredIntegrations = useMemo(
    () => Array.from(new Set(configuredInstances.map((instance) => instance.integration_name))),
    [configuredInstances]
  )

  /* Derived, not stored, so the checkbox always agrees with the source filter. */
  const allActiveSourcesChecked =
    uniqueConfiguredIntegrations.length > 0 &&
    uniqueConfiguredIntegrations.every((name) => filters.sources.includes(name))

  const fetchCategories = useCallback(async (): Promise<void> => {
    try {
      const res = await axios.get('/api/v3/frameworks/categories')

      if (res.status === 200) {
        setCategoryOptions(res.data.data.categories)
        setCategoriesLoaded(true)
      }
    } catch (err) {
      console.error(err)
    }
  }, [])

  const fetchConfiguredIntegrations = useCallback(async () => {
    const res = await axios.get<{ instances: IntegrationInstance[] }>('/api/v3/integrations/configured?loadStatus=true')

    if (res.status === 200) {
      const { instances } = res.data
      setConfiguredInstances(instances)
      setErroredInstances(instances.filter((n) => !!n.syncStatus).map((c) => ({ ...c.syncStatus, id: c.id })))
    }
  }, [])

  const fetchCustomFramework = useCallback(async (): Promise<void> => {
    try {
      const res = await axios.get<{ framework: IFramework }>('/api/v3/frameworks/custom')

      if (res.status.toString().includes('20') && res.data.framework) {
        setCustomFrameworkCategories(res.data.framework.data.functions)
      }
    } catch (err) {
      console.error(err)
    } finally {
      setFrameworkLoaded(true)
    }
  }, [])

  /* Only the unfiltered catalog contains every source. */
  const fetchCatalogSourceOptions = useCallback(async () => {
    const res = await axios.get('/api/v3/cpis/search?sortBy=desc')

    if (res.status !== 200) return

    setCatalogSourceOptions(collectSourceOptions(res.data.data.cpis))
  }, [])

  useEffect(() => {
    fetchCategories().catch((e) => e)
    fetchConfiguredIntegrations().catch((e) => e)
    fetchCatalogSourceOptions().catch((e) => e)
  }, [fetchCategories, fetchConfiguredIntegrations, fetchCatalogSourceOptions])

  useEffect(() => {
    if (isCustomFramework(framework) && !frameworkLoaded) {
      fetchCustomFramework().catch((e) => e)
    }
  }, [framework, frameworkLoaded, fetchCustomFramework])

  useEffect(() => {
    /* Narrow the options to the sources of the visible CPIs. Do not narrow with a source filter
     * applied: the results then contain only the selected sources, and the dropdown loses the
     * options that the user needs to change the selection. A status filter is safe, because the
     * status of a CPI does not limit its integrations. With no results, keep the current
     * options. */
    if (showSkeletons || data.length === 0 || filters.sources.length > 0) return

    setNarrowedSourceOptions(collectSourceOptions(data))
  }, [showSkeletons, data, filters.sources.length])

  const handleChangeFavorite = async (name: string, isFavorite: boolean) => {
    if (!isFavorite) {
      await axios.post('/api/v3/cpis/favorites', { name })
    } else {
      await axios.delete('/api/v3/cpis/favorites', { data: { name } })
    }
    refetch()
  }

  const isEmpty = !showSkeletons && data.length === 0

  return (
    <LibraryContainer>
      <HeaderComponent breadcrumbs={breadcrumbs.library} />

      <ToolbarComponent>
        <ToolbarWrapper>
          <Box className="line">
            <LibrarySearch
              placeholder="Search by CPI Name or Number…"
              options={[]}
              searchValue={searchInput}
              setSearchValue={setSearchInput}
            />
            <SelectorsWrapper>
              <LibrarySelectField
                options={categoryOptions.map((option) => option.title)}
                placeholder="None"
                version="category..."
                label={framework === CategoryFramework.NIST ? 'Functions' : 'Category'}
                value={selectedCategoryTitles}
                multiple
                handleChange={handleCategoryChange}
                handleReset={() => resetFilter('categoryIds')}
                minWidth="121px !important"
                maxHeight="unset"
                iscustom="true"
                field="category"
              />
              <LibrarySelectLabelField
                options={sourceOptions}
                placeholder="None"
                version="source..."
                label="Data Source"
                field="source"
                value={filters.sources}
                multiple
                handleChange={handleSourceChange}
                handleReset={() => resetFilter('sources')}
                minWidth="121px !important"
                iscustom="true"
                showActiveDataSources
                activeDataSourcesList={uniqueConfiguredIntegrations}
                allChecked={allActiveSourcesChecked}
              />
              <LibrarySelectLabelField
                options={defaultLibraryStatusOptions}
                placeholder="None"
                label="Status"
                value={filters.statuses}
                multiple
                handleChange={handleStatusChange}
                handleReset={() => resetFilter('statuses')}
                minWidth="100px !important"
                iscustom="true"
                hideSearch
              />

              <ClearFilters onClick={handleClearAll} disabled={!hasActiveLibraryFilters(filters) && !filters.term}>
                <ClearIcon />
                Clear
              </ClearFilters>
            </SelectorsWrapper>
          </Box>
          <ResultsWrapper ref={resultsWrapper}>
            <LibrarySelectField
              label=""
              options={librarySortOptionLabels}
              placeholder="Sort"
              noborder={'true'}
              isSort={true}
              value={SORT_LABELS[filters.sort]}
              handleChange={handleSortChange}
              handleReset={() => resetFilter('sort')}
              hideSearch={true}
            />
            <Typography className="results-text">
              {showSkeletons ? 0 : data.length} result{data.length !== 1 && 's'}
            </Typography>
          </ResultsWrapper>
        </ToolbarWrapper>
      </ToolbarComponent>

      <ContentWrapper line={2}>
        <>
          <LibraryContentWrapper>
            {showSkeletons && (
              <LoadingContainer>
                <LibraryCardLoading />
                <LibraryCardLoading />
                {!isSmallHeight && <LibraryCardLoading />}
                {!isMediumHeight && (
                  <>
                    <LibraryCardLoading />
                    <LibraryCardLoading />
                  </>
                )}
              </LoadingContainer>
            )}

            <ListWrapper>
              {!showSkeletons &&
                data.map((item) => {
                  const foundCategory = customFrameworkCategories.find((n) => n.id === (item.category?.id || ''))
                  const customColors = foundCategory
                    ? { chip_bg_color: foundCategory.chip_bg_color, chip_color: foundCategory.chip_color }
                    : undefined

                  return (
                    <Box className="no-underline" key={item.name}>
                      <LibraryCard
                        item={item}
                        handleChangeFavorite={handleChangeFavorite}
                        active={true}
                        loading={isFetching}
                        erroredInstances={erroredInstances}
                        customColors={customColors}
                        framework={framework}
                      />
                    </Box>
                  )
                })}

              {isEmpty && (
                <EmptyList>
                  <Typography className="heading">Sorry, we couldn't find anything.</Typography>
                  <ThemeButton onClick={handleClearAll}>Clear filters and try a new search</ThemeButton>
                </EmptyList>
              )}
            </ListWrapper>
          </LibraryContentWrapper>
          {isEmpty && <LibraryEmptyLogo fill={theme.colors.borderLow} />}
        </>
      </ContentWrapper>
    </LibraryContainer>
  )
}

export default Library
